<h2 mat-dialog-title>{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.TITLE' | translate }}</h2>

<mat-dialog-content>
  @if (!voiceOnly && modelsLoading()) {
    <div class="loading-container">
      <glxy-loading-spinner></glxy-loading-spinner>
    </div>
  } @else {
    @if (voiceOnly) {
      <section class="voice-configs">
        <p class="hint">{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.VOICE_CONFIGS_DESCRIPTION' | translate }}</p>

        @for (entry of voiceConfigEntries(); track entry; let i = $index) {
          <div class="voice-config-item">
            <div class="voice-config-header">
              <span class="index-label">
                @if (i === 0) {
                  {{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.VOICE_CONFIG' | translate }}
                } @else {
                  {{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.VOICE_CONFIG_N' | translate: { n: i + 1 } }}
                }
              </span>
              @if (voiceConfigEntries().length > 1) {
                <button
                  mat-icon-button
                  type="button"
                  (click)="removeVoiceConfig(entry.form)"
                  [attr.aria-label]="'AI_ASSISTANT.TESTING.RUN_OPTIONS.REMOVE_VOICE_CONFIG' | translate"
                >
                  <mat-icon>close</mat-icon>
                </button>
              }
            </div>

            <mat-button-toggle-group
              class="mode-toggle"
              activeColor="primary"
              [value]="entry.mode()"
              (change)="setEntryMode(entry, $event.value)"
            >
              <mat-button-toggle value="active">
                {{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.USE_ASSISTANT_SETTINGS' | translate }}
              </mat-button-toggle>
              <mat-button-toggle value="override">
                {{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.OVERRIDE' | translate }}
              </mat-button-toggle>
            </mat-button-toggle-group>

            @if (entry.mode() === 'active' && activeVoiceLabel) {
              <p class="active-voice-label">{{ activeVoiceLabel }}</p>
            }
            @if (entry.mode() === 'override') {
              <ai-voice-configuration-form [voiceConfigForm]="entry.form" />
            }
          </div>
        }

        <button mat-stroked-button type="button" (click)="addVoiceConfig()">
          <mat-icon>add</mat-icon>
          {{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.ADD_VOICE_CONFIG' | translate }}
        </button>
      </section>
    }

    @if (!voiceOnly) {
      <div class="model-section">
        <p class="hint">{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.DESCRIPTION' | translate }}</p>

        <div class="model-list">
          @for (model of modelSelections(); track model.id) {
            <div class="model-item" [class.selected]="model.selected">
              <mat-checkbox [checked]="model.selected" (change)="toggleModel(model)" class="model-checkbox">
                <span class="model-name">
                  {{ model.name }}
                  @if (model.isDefault) {
                    <glxy-badge color="blue-solid" size="small">{{
                      'AI_ASSISTANT.TESTING.RUN_OPTIONS.ACTIVE' | translate
                    }}</glxy-badge>
                    <mat-icon
                      inline="true"
                      [glxyTooltip]="'AI_ASSISTANT.TESTING.RUN_OPTIONS.ACTIVE_TOOLTIP' | translate"
                      >info</mat-icon
                    >
                  }
                </span>
              </mat-checkbox>

              @if (model.supportedReasoningEfforts.length > 0) {
                <div class="reasoning-toggles" [class.disabled]="!model.selected">
                  <span class="reasoning-label">{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.REASONING' | translate }}</span>
                  <glxy-button-group>
                    @for (effort of model.supportedReasoningEfforts; track effort) {
                      @if (isReasoningEffortSelected(model, effort)) {
                        <button
                          mat-flat-button
                          color="primary"
                          [disabled]="!model.selected"
                          (click)="toggleReasoningEffort(model, effort)"
                        >
                          {{ capitalizeFirst(effort) }}
                        </button>
                      } @else {
                        <button
                          mat-stroked-button
                          [disabled]="!model.selected"
                          (click)="toggleReasoningEffort(model, effort)"
                        >
                          {{ capitalizeFirst(effort) }}
                        </button>
                      }
                    }
                  </glxy-button-group>
                </div>
              }
            </div>
          }
        </div>

        @if (selectedCount() === 0) {
          <p class="warning">{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.SELECT_MODEL_WARNING' | translate }}</p>
        }
      </div>
    }

    <div class="iterations-section" [class.no-border]="voiceOnly">
      <glxy-form-field>
        <glxy-label>{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.ITERATIONS_LABEL' | translate }}</glxy-label>
        <input
          matInput
          type="number"
          [value]="iterations()"
          (input)="updateIterations($event)"
          min="1"
          max="25"
        />
        <glxy-hint>{{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.ITERATIONS_HINT' | translate }}</glxy-hint>
      </glxy-form-field>
    </div>

    @if (runPreviews().length > 0) {
      <div class="runs-preview">
        <h3>{{ 'AI_ASSISTANT.TESTING.RUN_CONFIGURATION.RUNS_TO_CREATE' | translate }} ({{ runPreviews().length }})</h3>
        <div class="run-list">
          @for (preview of runPreviews(); track $index) {
            <div class="run-item">
              <mat-icon>play_circle</mat-icon>
              <div class="run-details">
                @if (preview.isEditing) {
                  <mat-form-field appearance="outline" class="name-field">
                    <input
                      matInput
                      [ngModel]="preview.name"
                      (ngModelChange)="updatePreviewName(preview, $event)"
                      (blur)="toggleEditName(preview)"
                      (keyup.enter)="toggleEditName(preview)"
                    />
                  </mat-form-field>
                } @else {
                  <span class="run-name">{{ preview.name }}</span>
                }
                @if (preview.reasoningEffort) {
                  <span class="run-model">{{ preview.modelName }} ({{ capitalizeFirst(preview.reasoningEffort) }})</span>
                } @else if (preview.modelName && !voiceOnly) {
                  <span class="run-model">{{ preview.modelName }}</span>
                }
              </div>
              <button mat-icon-button (click)="toggleEditName(preview)" [disabled]="preview.isEditing">
                <mat-icon>edit</mat-icon>
              </button>
            </div>
          }
        </div>
      </div>
    }
  }
</mat-dialog-content>

<mat-dialog-actions>
  <button mat-stroked-button (click)="close()">{{ 'AI_ASSISTANT.SHARED.CANCEL' | translate }}</button>
  <button
    mat-raised-button
    color="primary"
    (click)="save()"
    [disabled]="voiceOnly ? !voiceConfigsValid() : !hasSelection()"
  >
    {{ 'AI_ASSISTANT.TESTING.RUN_OPTIONS.RUN_TESTS' | translate }}
    @if (!voiceOnly && selectedCount() > 0) {
      ({{ selectedCount() }}
      {{
        selectedCount() === 1
          ? ('AI_ASSISTANT.TESTING.RUN_OPTIONS.CONFIG' | translate)
          : ('AI_ASSISTANT.TESTING.RUN_OPTIONS.CONFIGS' | translate)
      }})
    }
  </button>
</mat-dialog-actions>
